import { Button, Icon, IdentityStack } from '@wtfalch/design';
import { useEffect, useState } from 'react';

import { formatDuration } from './CallDock.tsx';
import type { CallSessionValue } from './CallSession.tsx';
import { guestDisplayName } from './guestName.ts';

/** `name` is already cleaned (`guestDisplayName`); this just takes its first word. */
function firstName(name: string): string {
  return name.split(' ')[0] ?? name;
}

/**
 * The call, drawn inside the room it was started from: how long it has run,
 * who is on it, who is talking, and the controls that matter without opening
 * it full. Joined by `CallSurface` while the full call view is closed.
 */
export function RoomCallCard({
  session,
  startedAt,
  onBook,
  className,
}: {
  session: CallSessionValue;
  startedAt: number;
  /** Offered where the call has no meeting yet. Absent on an event's own call. */
  onBook?: () => void;
  className?: string;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const participants = session.stats?.participants ?? [];
  const self = participants.find((person) => person.self);
  const muted = self?.media ? !self.media.microphone : false;
  const talking = participants.find((person) => person.media?.speaking && !person.self);
  const { endpoint } = session;
  const waiting = session.lobby?.waiting.length ?? 0;

  return (
    <section
      aria-label="The call"
      className={['flex flex-col gap-3 rounded-md border border-border p-3', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="flex flex-wrap items-center gap-3">
        <IdentityStack
          people={participants.map((person) => ({
            name: guestDisplayName(person.displayName, 'Someone'),
            address: person.participantId,
          }))}
          max={3}
          size="sm"
        />
        <span className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-strong">
            {session.joining
              ? 'Joining the call…'
              : `On a call · ${formatDuration(now - startedAt)}`}
          </span>
          <span className="text-xs text-muted" role="status">
            {waiting > 0
              ? (session.lobby?.notice ?? `${waiting} waiting to join`)
              : talking
                ? `${firstName(guestDisplayName(talking.displayName, 'Someone'))} is talking`
                : ''}
          </span>
        </span>
        <Button
          size="sm"
          aria-pressed={muted}
          disabled={!endpoint || session.joining}
          onPress={() => {
            endpoint?.setMicrophoneEnabled(muted).catch((error: unknown) => {
              console.error('Could not change the microphone:', error);
            });
          }}
        >
          <Icon name={muted ? 'mic-off' : 'mic'} size={16} />
          <span>{muted ? 'Unmute' : 'Mute'}</span>
        </Button>
        <Button size="sm" onPress={() => session.setFocused(true)}>
          Open it full
        </Button>
        <Button kind="danger" size="sm" onPress={session.leave}>
          <Icon name="phone-down" size={16} />
          <span>Leave</span>
        </Button>
      </div>
      {onBook && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="m-0 flex-1 text-xs text-muted">
            Nobody booked this one. It can still become a meeting, without hanging up.
          </p>
          <Button size="sm" onPress={onBook}>
            Make it a meeting
          </Button>
        </div>
      )}
    </section>
  );
}
